import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import _ from 'lodash'
import toast from 'react-hot-toast'
import { v4 } from 'uuid'

import api from 'Src/api'

import ToggleSwitch from 'Src/components/Toggle'

import TrixEditor from '../components/Trix'
import { Prompt } from 'Src/history'

import backArrow from '../assets/images/back-arrow.svg?url'

const PATCH_ANSWER_URL = 'v1/answers/:answer_id'
const CREATE_ANSWER_URL = 'v1/answers/'
const UPLOAD_IMAGE_URL = 'v1/answers/upload/'
const FETCH_SPECIFIC_ANSWER_URL = 'v1/answers/:answer_id'

const toastId = v4()

const showErrorToast = (error, defaultMessage = 'Something went wrong. Try again') => {
  const data = _.get(error, 'response.data', {})
  let errorMsg = defaultMessage

  switch (data.type) {
    case 'ANSWER_ALREADY_EXISTS':
      errorMsg = data.message
      break

    default:
      errorMsg = defaultMessage
  }

  toast.error(errorMsg, { id: toastId })
}

const Answers = (props) => {
  const { isEditMode = false } = props

  const navigate = useNavigate()
  const params = useParams()

  const isCreateMode = !isEditMode

  const [answer, setAnswer] = useState(null)
  const [answerTitle, setAnswerTitle] = useState('')
  const [answerActiveState, setAnswerActiveState] = useState(false)
  const [feedback, setFeedback] = useState(false)
  const [answerTags, setAnswerTags] = useState([])
  const [content, setContent] = useState('')
  const [uploadingImage, setUploadingImage] = useState(false)
  const [isContentChange, setIsContentChange] = useState(false)
  const [titleErrorMessage, setTitleErrorMessage] = useState('')

  const contentOrTitleMissing = !(content && answerTitle)
  const disableSaveButton = contentOrTitleMissing || uploadingImage || !isContentChange

  const resetState = useCallback(() => {
    setContent('')
    setAnswerTitle('')
    setAnswerActiveState(false)
    setFeedback(false)
    setAnswerTags([])
    setUploadingImage(false)
    setIsContentChange(false)
  }, [])

  useEffect(() => {
    if (isCreateMode) {
      // Clear the state user navigates to create answer
      resetState()
    }
  }, [isCreateMode, resetState])

  // used in editMode
  const answerId = params.id

  const fetchAnswer = useCallback(async () => {
    const response = await api.get(FETCH_SPECIFIC_ANSWER_URL, {
      urlParams: {
        answer_id: answerId
      }
    })
    const answer = response.data
    setAnswer(answer)
  }, [answerId])

  /**
   * When page is refreshed, answers in store is reset to empty.
   * We need to fetch the answers again.
   */
  useEffect(() => {
    if (isEditMode) {
      fetchAnswer()
    }
  }, [fetchAnswer, isEditMode, answerId])

  useEffect(() => {
    if (!isEditMode || _.isEmpty(answer)) {
      return
    }

    const name = _.get(answer, 'name')
    const answerContent = _.get(answer, 'answer')
    const answerState = _.get(answer, 'is_active')
    const answerFeedback = _.get(answer, 'feedback')
    const answerTags = _.get(answer, 'tags')

    setAnswerTitle(name)
    setAnswerActiveState(answerState)
    setFeedback(answerFeedback.enabled)
    setAnswerTags(answerTags)
    setContent(answerContent)
  }, [answer, isEditMode])

  const onAnswerTitleChange = (event) => {
    const { value } = event.target
    setAnswerTitle(value)
  }

  const onAnswerTitleSave = (event) => {
    const { value } = event.target
    const name = _.get(answer, 'name')
    if (value.length < 3) {
      setTitleErrorMessage('Title must contain atleast 3 characters')
      return
    }
    setTitleErrorMessage('')
    if (!isCreateMode && answerTitle !== name) {
      saveChange('answerTitle', answerTitle)
    }
  }

  const saveChange = (property, value) => {
    if (!isCreateMode) {
      const payload = {
        answerTitle,
        content,
        answerActiveState,
        feedback,
        answerTags
      }
      payload[property] = value
      handleAnswerAPICall('update', payload)
      if (property === 'feedback') setFeedback(value)
      else if (property === 'answerTags') setAnswerTags(value)
      else if (property === 'answerActiveState') setAnswerActiveState(value)
    }
  }

  const handleUploadImage = async (event) => {
    const attachment = event.attachment
    if (attachment.file) {
      try {
        setUploadingImage(true) // Disable Save button while image is being uploaded
        const formData = new FormData()
        formData.append('file', attachment.file)
        const response = await api.post(UPLOAD_IMAGE_URL, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        const { fileUrl } = response.data
        attachment.setAttributes({
          url: fileUrl,
          href: fileUrl
        })
      } catch (error) {
        console.error('Error while uploading image to server', error)
      } finally {
        setUploadingImage(false) // Enable Save button in case of error or success of API call
      }
    }
  }

  const handleContentChange = (text) => {
    if (content !== text) {
      setIsContentChange(true)
    }
    setContent(text)
  }

  const handleSave = () => {
    const payload = {
      answerTitle,
      content,
      answerActiveState,
      feedback,
      answerTags
    }
    const action = answerId ? 'update' : 'create'
    handleAnswerAPICall(action, payload)
  }

  const handleAnswerAPICall = async (action, payload) => {
    const { answerTitle, content, answerActiveState, feedback, answerTags } = payload

    const reqPayload = {
      name: answerTitle,
      is_active: answerActiveState,
      content,
      feedback: {
        enabled: feedback,
        settings: {}
      },
      tags: answerTags
    }
    try {
      if (action === 'update') {
        await api.patch(PATCH_ANSWER_URL, reqPayload, { urlParams: { answer_id: answerId } })
        toast.success('Updated Successfully', { id: toastId })
      } else {
        const response = await api.post(CREATE_ANSWER_URL, reqPayload)
        setIsContentChange(false)
        if (isCreateMode) {
          resetState()
          navigate(`/answers/edit/${response.data.id}`, { replace: true })
        }
        toast.success('Created Successfully', { id: toastId })
      }
    } catch (error) {
      console.error(`Error while ${action}ing an answer`, error)
      const data = _.get(error, 'response.data', {})

      switch (data.type) {
        case 'ANSWER_ALREADY_EXISTS':
          setTitleErrorMessage(data.message)
          break
      }

      showErrorToast(error, `Error while ${action === 'update' ? 'updating' : 'creating'} answer. Try again...`)
    } finally {
      setIsContentChange(false)
    }
  }

  const renderCreateModeEditor = (
    <React.Fragment>
      <TrixEditor
        answerId={answerId}
        defaultValue=''
        answerTitle=''
        contentChangeHandler={handleContentChange}
        uploadImage={handleUploadImage}
        saveStepButtonText='Save'
      />
    </React.Fragment>
  )

  const renderEditModeEditor = content ? (
    <TrixEditor
      answerId={answerId}
      defaultValue={content}
      answerTitle={answerTitle}
      contentChangeHandler={handleContentChange}
      uploadImage={handleUploadImage}
      saveStepButtonText='Save'
    />
  ) : (
    renderCreateModeEditor
  )

  return (
    <div className='page-container answers'>
      <div className='main-content'>
        <div className='page-header'>
          <Link className='back-button mr-3' to='/answers'>
            <img src={backArrow} />
          </Link>
          <div className='section-heading'>
            <div className='section-name'>Answer Title</div>
            <div className='section-title'>
              <input
                placeholder='Enter Answer title'
                name='name'
                value={answerTitle}
                onChange={onAnswerTitleChange}
                onBlur={onAnswerTitleSave}
                style={{ outline: '0', width: '100%', border: '0' }}
              />
              {titleErrorMessage && <small className='text-danger'>{titleErrorMessage}</small>}
            </div>
          </div>
        </div>
        <div className='answer'>
          <div className='answer-content-editor'>
            {isCreateMode ? renderCreateModeEditor : renderEditModeEditor}
            <button className='button-primary' disabled={disableSaveButton} onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </div>
      <div className='sidebar-container'>
        <div className='settings-heading'>Manage</div>
        <div className='settings-row clearfix'>
          <div className='settings-label'>Active</div>
          <div className='settings-switch'>
            <ToggleSwitch
              checked={answerActiveState}
              onClick={() => saveChange('answerActiveState', !answerActiveState)}
            />
          </div>
        </div>
      </div>
      <Prompt when={!disableSaveButton} message='Are you sure you want to discard your unsaved changes?' />
    </div>
  )
}

export default Answers
